/**
 * What a customer's page says about them, worked out from its reads
 * (useCustomerProfile): the summary, both halves of their orders, and their
 * money. Pure functions, so the header, the attention strip and the tabs say
 * the same thing.
 */

import { caseDeadline } from '../customerService/disputes/disputesShared';
import { STATUS_PREFIX, ownStatus, statusLabel } from '../order-workflow/orderStatus';

const time = (value) => (value ? new Date(value).getTime() : NaN);
const known = (value) => !Number.isNaN(time(value));

export const customerName = (c) => (c && (c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' '))) || 'Customer';

/** The person's name and, when there's both, the company under it. */
export function nameLines(c) {
  const person = [c?.first_name, c?.last_name].filter(Boolean).join(' ');
  if (person && c?.company_name) return { title: person, subtitle: c.company_name };
  return { title: person || c?.company_name || 'Customer', subtitle: null };
}

export const initials = (c) => {
  const words = [c?.first_name, c?.last_name].filter(Boolean);
  const from = words.length ? words : String(c?.company_name || '?').split(/\s+/).slice(0, 2);
  return from.map((w) => w[0]).join('').toUpperCase() || '?';
};

/** An order as people say it: its kind's prefix (RO, EST, INV) and number for Chariot's; the number alone for Shopmonkey's. */
export const orderNumberText = (o) => (o.source === 'shopmonkey'
  ? `#${o.number}`
  : `${STATUS_PREFIX[ownStatus(o.status)] || ''} #${o.order_number}`.trim());

/** What the order is for: its title (Chariot) or name (Shopmonkey), else its status in words. */
export const orderWhat = (o) => o.title || o.name || o.workflow_status_name || (o.source === 'shopmonkey' ? o.status : statusLabel(o.status));

const SM_STATUS = { Estimate: 'Estimate', RepairOrder: 'Repair Order', Invoice: 'Invoice' };

/**
 * An order's status in words and its chip's tone ([text, tone]): an unpaid
 * invoice first; else Shopmonkey's column or kind (paid or not), or Chariot's
 * column or status.
 */
export function orderStatusChip(o) {
  if (o.unpaid) return ['Invoice · unpaid', 'crit'];
  if (o.source === 'shopmonkey') {
    if (o.status === 'Invoice') return [o.paid || o.fully_paid_at ? 'Paid' : 'Invoice', o.paid || o.fully_paid_at ? 'good' : 'warn'];
    return [o.workflow_status || SM_STATUS[o.status] || o.status || '—', 'info'];
  }
  const tone = { estimate: 'info', repair_order: 'warn', invoice: 'good', archive: 'sm' }[ownStatus(o.status)] || 'sm';
  return [o.workflow_status_name || statusLabel(o.status), tone];
}

/** Every order shown, both halves, newest first. */
export function allOrders(orders) {
  const rows = [...(orders?.chariot?.rows || []), ...(orders?.shopmonkey?.rows || [])];
  return rows.sort((a, b) => (time(b.created_at) || 0) - (time(a.created_at) || 0));
}

/** When they became a customer: Chariot's answer (summary) or Shopmonkey's, the earlier. */
export function customerSince(summary, orders) {
  const dates = [summary?.customer_since, orders?.shopmonkey?.since].filter(known);
  return dates.length ? new Date(Math.min(...dates.map(time))) : null;
}

/**
 * When work was last done: the newest order finished or invoiced, either half
 * (Chariot's completed or invoiced date; an invoice with neither counts from
 * when it was made).
 */
export function lastService(orders) {
  const done = [
    ...(orders?.chariot?.rows || []).map((o) => o.completed_at || (o.invoiced ? o.created_at : null)),
    ...(orders?.shopmonkey?.rows || []).map((o) => o.completed_at || o.invoiced_at),
  ].filter(known);
  return done.length ? new Date(Math.max(...done.map(time))) : null;
}

/** Orders still open, either half, newest first. */
export const openOrders = (orders) => allOrders(orders).filter((o) => o.open);

/**
 * What they owe: Chariot's invoices (the summary, or the money read when it's
 * there) and Shopmonkey's (only from the money read). Null without money.
 */
export function balanceOwed(summary, money) {
  if (!summary?.money) return null;
  const b = money?.data?.balance;
  const chariot = b ? b.chariot_cents : summary.money.balance_cents;
  const invoices = (b ? b.chariot_invoices : summary.money.balance_invoices) || 0;
  const sm = b?.shopmonkey_cents || 0;
  return {
    cents: (chariot || 0) + sm,
    invoices: invoices + (b?.shopmonkey_invoices || 0),
    shopmonkeyKnown: !!b && b.shopmonkey_cents !== null,
  };
}

/** The last charge tried and how it went: the money read's (Shopmonkey's too) or the summary's. */
export const lastCharge = (summary, money) => money?.data?.last_charge || summary?.money?.last_charge || null;

/**
 * What's missing from the record, from the customer as shown now (so it
 * updates the moment one is added): no phone, no email, no address, a boat
 * with no place it's kept — or, when the customer said no, that they declined
 * (a fact, not a gap).
 */
export function missingInfo(c) {
  const out = [];
  const has = (rows, key) => (rows || []).some((r) => String(r[key] || '').trim());
  if (!has(c?.phones, 'phone_number')) out.push(c?.phone_declined_at ? { key: 'phone', label: 'Declined to share a phone', declined: true } : { key: 'phone', label: 'No phone' });
  if (!has(c?.emails, 'email')) out.push(c?.email_declined_at ? { key: 'email', label: 'Declined to share an email', declined: true } : { key: 'email', label: 'No email' });
  if (!String(c?.address1 || '').trim()) out.push({ key: 'address', label: 'No address' });
  const unplaced = (c?.vehicles || []).filter((v) => !v.deleted && !v.location_kind && !v.marina).length;
  if (unplaced) out.push({ key: 'boat_place', label: unplaced === 1 ? 'A boat with no location' : `${unplaced} boats with no location` });
  return out;
}

/**
 * A dispute's respond-by day in words ("Nov 4, 2026"), read as the Disputes
 * Center reads it — the stored day, never shifted by the viewer's time zone
 * (disputesShared.js caseDeadline). Null when there's none.
 */
export function respondByText(dispute) {
  const deadline = caseDeadline(dispute || {});
  if (!deadline) return null;
  const [y, m, d] = deadline.day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * A flag's last day in words: it ends at midnight Eastern after the day it
 * was set to hold through, so the moment before is that day ("Nov 4", never
 * the Nov 5 it ends on). FlagEditor's date box reads it the same way.
 */
export const flagLastDay = (endsAt) => (endsAt
  ? new Date(new Date(endsAt).getTime() - 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' })
  : null);

/**
 * The next visit booked (the summary's next_visit) in words: `when` ("Thu,
 * Oct 31, 9:00 AM"; the day alone for an all-day one) and `detail` (who's
 * going, the boat, the order). Null when none is booked.
 */
export function nextVisitText(v) {
  if (!v) return null;
  const start = new Date(v.start_date);
  const dayText = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const techs = (v.technicians || []).map((t) => [t.first_name, t.last_name && `${t.last_name[0]}.`].filter(Boolean).join(' ')).join(', ');
  return {
    when: v.all_day ? dayText : `${dayText}, ${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`,
    detail: [techs, v.vehicle_name, v.order_number && `Order #${v.order_number}`].filter(Boolean).join(' · '),
  };
}

/** A phone or email list's main row (★), else the first. */
export const mainRow = (rows) => (rows || []).find((r) => r.primary) || (rows || [])[0] || null;

/** "Visa ••4242". */
export const cardText = (brand, last4) => (last4 ? `${brand ? `${String(brand).replace(/^\w/, (c) => c.toUpperCase())} ` : ''}••${last4}` : null);
